import { useEffect, useState } from "react";
import axios from "axios";
import { BACKEND_URL, API } from "@/lib/api";
import { Logo } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pill, MapPin, MagnifyingGlass, DownloadSimple, Phone, EnvelopeSimple, Clock, PaperPlaneTilt } from "@phosphor-icons/react";

const CONTACTS = {
  emails: [
    "OPS@HELIOPOLISPHARMACEUTICALS.COM",
    "CUSTOMERCARE@HELIOPOLISPHARMACEUTICALS.COM",
    "SALES@HELIOPOLISPHARMACEUTICALS.COM",
  ],
  phones: [
    { name: "JOSEPH GADALLA", number: "00971547741436" },
    { name: "JOHN KHAIRY", number: "00971505348137" },
  ],
};

function PublicImg({ id, hasPhoto, full = false, testid }) {
  if (!hasPhoto) {
    return (
      <div className={full ? "h-64 bg-zinc-100 rounded-md flex items-center justify-center" : "aspect-square bg-zinc-100 flex items-center justify-center"}>
        <Pill size={40} className="text-zinc-300" weight="duotone" />
      </div>
    );
  }
  const src = `${API}/public/catalogue/${id}/photo`;
  if (full) return <img src={src} alt="Product" className="max-h-[70vh] w-full object-contain rounded-md bg-zinc-100" data-testid={testid} />;
  return <img src={src} alt="Product" className="aspect-square w-full object-cover cursor-zoom-in transition-transform hover:scale-[1.03]" data-testid={testid} />;
}

export default function PublicCatalogue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/catalogue`)
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const q = search.trim().toLowerCase();
  const shown = q ? items.filter((it) => (it.name || "").toLowerCase().includes(q)) : items;

  const download = (id, name) => {
    const a = document.createElement("a");
    a.href = `${API}/public/catalogue/${id}/photo`;
    a.download = `${name}.jpg`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const enquireHref = `mailto:${CONTACTS.emails[2].toLowerCase()}?cc=${CONTACTS.emails[1].toLowerCase()}&subject=${encodeURIComponent("Catalogue enquiry / access request — Heliopolis Pharmaceuticals")}&body=${encodeURIComponent("Hello Heliopolis team,\n\nI would like to enquire about the following products / request access to your portal:\n\n- \n\nCompany name:\nContact person:\nPhone:\nCountry:\n\nThank you.")}`;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="public-catalogue-page">
      {/* Header */}
      <header className="text-white" style={{ background: "linear-gradient(180deg, hsl(211 68% 21%) 0%, hsl(213 66% 16%) 55%, hsl(213 70% 13%) 100%)" }}>
        <div className="max-w-6xl mx-auto px-5 py-10 flex flex-col items-center text-center gap-5">
          <div className="hp-logo-chip inline-flex rounded-xl bg-white px-6 py-4" style={{ boxShadow: "0 6px 20px hsl(213 70% 8% / 0.35)" }}>
            <Logo className="h-24" />
          </div>
          <span className="flex items-center gap-2 text-white/90"><Clock size={18} weight="bold" /><span className="font-semibold tracking-wide">WORKING 24/7 FOR A BETTER TOMORROW</span></span>
          <a href={enquireHref} data-testid="enquire-header-btn">
            <Button className="rounded-sm" style={{ backgroundColor: "hsl(43 91% 55%)", color: "hsl(30 45% 15%)" }}><PaperPlaneTilt size={16} className="mr-2" /> Request Access / Enquire</Button>
          </a>
        </div>
      </header>

      {/* Title band */}
      <div className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-5 py-8">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Heliopolis Pharmaceuticals</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl mt-1">Product Catalogue</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Browse our full range of pharmaceutical products. For orders, pricing and availability, please get in touch with our team below.</p>
          <a href={enquireHref} data-testid="enquire-title-btn" className="inline-block mt-4">
            <Button className="rounded-sm bg-primary hover:bg-primary/90"><PaperPlaneTilt size={16} className="mr-2" /> Request Access / Enquire</Button>
          </a>
          {items.length > 0 && (
            <div className="relative max-w-md mt-5">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9 rounded-sm bg-white" placeholder="Search products…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="public-catalogue-search" />
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <main className="max-w-6xl mx-auto px-5 py-8">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square bg-zinc-100 rounded-md animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">No products available yet. Please check back soon.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {shown.map((it) => (
              <div key={it.id} className="border border-border rounded-md bg-white overflow-hidden flex flex-col" data-testid={`public-catalogue-card-${it.name}`}>
                <div className="relative" onClick={() => it.has_photo && setZoom(it)}>
                  <PublicImg id={it.id} hasPhoto={it.has_photo} testid={`public-catalogue-photo-${it.name}`} />
                  {it.has_photo && (
                    <button
                      onClick={(e) => { e.stopPropagation(); download(it.id, it.name); }}
                      className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-xs px-2.5 py-1 hover:bg-black/80 transition-colors"
                      data-testid={`public-download-photo-${it.name}`}
                    >
                      <DownloadSimple size={12} /> Download
                    </button>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="font-heading font-bold">{it.name}</p>
                  {it.origin && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={12} /> Origin: {it.origin}</p>}
                  {it.note && <p className="text-sm text-muted-foreground mt-2 flex-1">{it.note}</p>}
                </div>
              </div>
            ))}
            {shown.length === 0 && (
              <div className="col-span-full py-10 text-center text-muted-foreground" data-testid="public-catalogue-no-results">No products match "{search}".</div>
            )}
          </div>
        )}
      </main>

      {/* Contact footer */}
      <footer className="text-white mt-8" style={{ background: "linear-gradient(180deg, hsl(211 68% 21%) 0%, hsl(213 70% 13%) 100%)" }} data-testid="public-catalogue-contact">
        <div className="max-w-6xl mx-auto px-5 py-10">
          <h2 className="font-heading font-bold text-xl">Get in touch</h2>
          <div className="grid sm:grid-cols-2 gap-8 mt-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/60 mb-3">Email us</p>
              <ul className="space-y-2">
                {CONTACTS.emails.map((e) => (
                  <li key={e}>
                    <a href={`mailto:${e.toLowerCase()}`} className="inline-flex items-center gap-2 text-sm hover:underline break-all">
                      <EnvelopeSimple size={16} className="shrink-0" /> {e}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/60 mb-3">Call us</p>
              <ul className="space-y-2">
                {CONTACTS.phones.map((p) => (
                  <li key={p.number}>
                    <a href={`tel:+${p.number}`} className="inline-flex items-center gap-2 text-sm hover:underline">
                      <Phone size={16} className="shrink-0" /> <span className="font-semibold">{p.name}</span> — +{p.number}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-8 pt-6 border-t border-white/15 text-white/70 text-sm">© {new Date().getFullYear()} Heliopolis Pharmaceuticals · Working 24/7 for a better tomorrow.</p>
        </div>
      </footer>

      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-3xl" data-testid="public-catalogue-photo-dialog">
          <DialogHeader><DialogTitle>{zoom?.name}</DialogTitle></DialogHeader>
          {zoom && <PublicImg id={zoom.id} hasPhoto={zoom.has_photo} full testid="public-catalogue-zoom-img" />}
          {zoom?.origin && <p className="text-sm text-muted-foreground flex items-center gap-1.5"><MapPin size={14} /> {zoom.origin}</p>}
          {zoom?.note && <p className="text-sm text-muted-foreground">{zoom.note}</p>}
          {zoom?.has_photo && (
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => download(zoom.id, zoom.name)} data-testid="public-catalogue-photo-download"><DownloadSimple size={16} className="mr-2" /> Download</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
